import { CSAT_MAX_RATING, CSAT_MIN_RATING, CSAT_RATING_LABELS } from '@gb-ticket/shared-types';
import { Loader2Icon, StarIcon } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export function CsatSurveyForm({
  initialRating,
  pending,
  onSubmit,
}: {
  initialRating?: number | null;
  pending: boolean;
  onSubmit: (input: { rating: number; comment?: string }) => void;
}) {
  const copy = useVoiceCopy();
  const [rating, setRating] = useState<number | null>(
    initialRating != null && initialRating >= CSAT_MIN_RATING && initialRating <= CSAT_MAX_RATING
      ? initialRating
      : null,
  );
  const [comment, setComment] = useState('');

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (rating == null) {
          return;
        }
        onSubmit({ rating, comment: comment.trim() || undefined });
      }}
    >
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">{copy('helpdesk.csat.legend')}</legend>
        <div className="flex flex-wrap gap-1">
          {Array.from({ length: CSAT_MAX_RATING }, (_, index) => {
            const value = index + 1;
            const selected = rating != null && value <= rating;
            return (
              <button
                key={value}
                type="button"
                aria-label={`${value} ${value === 1 ? 'Stern' : 'Sterne'} – ${CSAT_RATING_LABELS[value]}`}
                aria-pressed={rating === value}
                onClick={() => setRating(value)}
                className="rounded-md p-1 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
              >
                <StarIcon
                  className={cn(
                    'size-8',
                    selected ? 'fill-amber-400 text-amber-500' : 'text-muted-foreground',
                  )}
                  aria-hidden
                />
              </button>
            );
          })}
        </div>
        {rating != null && (
          <p className="text-sm text-muted-foreground">{CSAT_RATING_LABELS[rating]}</p>
        )}
      </fieldset>
      <div className="space-y-1.5">
        <Label htmlFor="csat-comment">Kommentar (optional)</Label>
        <textarea
          id="csat-comment"
          value={comment}
          maxLength={2_000}
          rows={3}
          onChange={(event) => setComment(event.target.value)}
          className="min-h-20 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30"
        />
      </div>
      <Button type="submit" disabled={pending || rating == null}>
        {pending && <Loader2Icon className="animate-spin" aria-hidden />}
        Bewertung senden
      </Button>
    </form>
  );
}
